import dayjs from 'dayjs'
import 'dayjs/locale/zh-cn'
import relativeTime from 'dayjs/plugin/relativeTime'
import { cloneDeep, debounce, isEqual, merge, throttle } from 'lodash-es'
import { isOssSignedUrl } from './ossSignedUrl'
import { DEFAULT_STATIC_ASSET_ORIGIN } from './staticAssetConfig'
import { shouldDisableStaticAssetOssFormat } from './staticAssetManifest'
import { EMPTY_VALUE_PLACEHOLDER } from './constant'
import {
  resolveGiveawayGiftLineValue,
  resolveGiveawayGiftUnitPrice,
} from '~/utils/store/giveawayGiftDisplay'

// 扩展插件
dayjs.extend(relativeTime)

// 设置默认语言
dayjs.locale('zh-cn')

/**
 * @description 手机号正则校验
 */
export const MOBILE_REGEX = /^1[3-9]\d{9}$/

/**
 * @description 数据深拷贝工具函数
 */
export const deepClone = (obj) => cloneDeep(obj)

/**
 * @description 对象深比较工具函数
 * @param {*} value - 要比较的值
 * @param {*} other - 另一个要比较的值
 * @returns {boolean} - 是否相等
 */
export const isDeepEqual = (value, other) => isEqual(value, other)

/**
 * @description 对象合并工具函数
 */
export const mergeObjects = (target, ...sources) => merge(target, ...sources)

/**
 * @description 日期格式化工具函数
 */
export const formatDate = (date, format = 'YYYY-MM-DD HH:mm:ss') => {
  if (!date) return EMPTY_VALUE_PLACEHOLDER
  return dayjs(date).format(format)
}

/**
 * @description 订单列表日期：今年订单省略年份，往年订单展示年份
 */
export const formatOrderListDate = (date) => {
  if (!date) return EMPTY_VALUE_PLACEHOLDER
  const target = dayjs(date)
  if (!target.isValid()) return EMPTY_VALUE_PLACEHOLDER
  return target.year() === dayjs().year()
    ? target.format('MM-DD HH:mm:ss')
    : target.format('YYYY-MM-DD HH:mm:ss')
}

/**
 * @description 相对时间工具函数
 */
export const fromNow = (date) => {
  if (!date) return EMPTY_VALUE_PLACEHOLDER
  return dayjs(date).fromNow()
}

/**
 * @description 重新导出 Lodash 工具函数
 */
export { debounce, throttle }

/**
 * @description 重新导出脱敏工具函数
 */
export * from './desensitize'
export * from './phone'
export * from './addressDelivery'

/**
 * @description 基础类型检查
 */
export const isArray = Array.isArray
export const isObject = (val) => val !== null && typeof val === 'object'
export const isString = (val) => typeof val === 'string'
export const isNumber = (val) => typeof val === 'number'

const parsePriceNumber = (price) => {
  if (price === undefined || price === null || price === '') return NaN
  const normalized = typeof price === 'string' ? price.replace(/[¥￥,\s]/g, '').trim() : price
  const num = Number(normalized)
  return Number.isFinite(num) ? num : NaN
}

/**
 * @description 格式化价格，保留两位小数并添加千分位分隔符
 * @param {number|string} price - 价格
 * @returns {string} - 格式化后的价格 (例如: 1,234,567.89)
 */
export const formatPrice = (price) => {
  const num = parsePriceNumber(price)
  if (isNaN(num)) return '0.00'

  return num.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

/**
 * @description 商品/活动展示价格：最多两位小数，不补尾零。
 * @param {number|string} price - 价格
 * @returns {string} - 格式化后的价格 (例如: 1,234 / 1,234.5 / 1,234.56)
 */
export const formatPriceCompact = (price) => {
  const num = parsePriceNumber(price)
  if (isNaN(num)) return '0'

  return num.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })
}

const PRICE_TEXT_NUMBER_RE = /(?:\d{1,3}(?:,\d{3})+|\d+)(?:\.\d+)?/g
const PRICE_TEXT_ONLY_NUMBER_RE = /^(?:\d{1,3}(?:,\d{3})+|\d+)(?:\.\d+)?$/

/**
 * @description 将价格文本中的数值统一转成“不补零、最多两位”的展示格式。
 * @param {number|string} value - 价格或价格文案
 * @returns {string}
 */
export const formatCompactPriceText = (value) => {
  const text = String(value ?? '').trim()
  if (!text) return ''
  return text.replace(PRICE_TEXT_NUMBER_RE, (match) => formatPriceCompact(match))
}

/**
 * @description 拆分商品/活动价格，供整数和小数字号分层展示。
 * @param {number|string} value - 价格或价格文案
 * @returns {{ integer: string, decimal: string }} decimal 含小数点；无有效小数时为空字符串。
 */
export const splitCompactPriceParts = (value) => {
  const text = formatCompactPriceText(value)
  if (!text) return { integer: '', decimal: '' }
  if (!PRICE_TEXT_ONLY_NUMBER_RE.test(text)) return { integer: text, decimal: '' }
  const [integer = '0', decimal = ''] = text.split('.')
  return {
    integer,
    decimal: decimal ? `.${decimal}` : '',
  }
}

/**
 * @description 赠品行小计（与订单确认 `resolveGiveawayGiftLineValue` 一致）
 * @param {Object} g - 赠品项
 * @returns {number}
 */
export function getGiftLineSubtotal(g) {
  return resolveGiveawayGiftLineValue(g)
}

/**
 * @description 赠品展示单价（与订单确认 `resolveGiveawayGiftUnitPrice` 一致）
 * @param {Object} g - 赠品项
 * @returns {number}
 */
export function getGiftUnitPriceForDisplay(g) {
  return resolveGiveawayGiftUnitPrice(g)
}

/**
 * @description 单条赠品价值（小计），与 `getGiftLineSubtotal` 一致；总额 = 各条相加
 * @param {Object} g - 赠品项
 * @returns {number}
 */
export const getGiftItemValue = (g) => resolveGiveawayGiftLineValue(g)

/**
 * @description URL 参数获取工具函数
 */
export const getQueryParam = (name) => {
  if (!import.meta.client) return null
  const urlParams = new URLSearchParams(window.location.search)
  return urlParams.get(name)
}

/**
 * @description 将站内相对地址转换为完整 URL，主要用于分享/二维码/复制链接
 * @param {string} url - 原始地址
 * @param {string} [fallback=''] - 原始地址为空或解析失败时的兜底地址
 * @returns {string} 完整 URL
 */
export const getAbsoluteUrl = (url, fallback = '') => {
  const rawUrl = String(url || '').trim()
  const fallbackUrl = String(fallback || '').trim()

  if (!import.meta.client) {
    return rawUrl || fallbackUrl
  }

  const targetUrl = rawUrl || fallbackUrl || window.location.href

  try {
    return new URL(targetUrl, window.location.href).href
  } catch (error) {
    return fallbackUrl || window.location.href
  }
}

const PLACEHOLDER_IMAGE_HOSTS = new Set(['example.com', 'www.example.com'])

/**
 * @description 判断接口图片是否来自不可用于页面展示的演示占位域名
 * @param {string} imageUrl - 待检查的图片地址
 * @returns {boolean} 是否为占位图片地址
 */
export const isPlaceholderImageUrl = (imageUrl) => {
  if (!imageUrl || typeof imageUrl !== 'string') {
    return false
  }

  try {
    return PLACEHOLDER_IMAGE_HOSTS.has(new URL(imageUrl).hostname.toLowerCase())
  } catch {
    return false
  }
}

const OSS_DOMAIN = 'oss-cn-shenzhen.aliyuncs.com'

/** 展示图最大宽度，与 getOssLimitedWidthUrl 一致；带 format,avif/webp 时必须带此限宽，避免请求原图全尺寸 */
const OSS_DISPLAY_RESIZE_W1280 = 'resize,w_1280'

/**
 * @description 判断 URL 是否为阿里云 OSS 地址
 * @param {string} url - 图片地址
 * @returns {boolean}
 */
export const isOssUrl = (url) => {
  if (!url || typeof url !== 'string') return false
  return url.includes(OSS_DOMAIN)
}

/**
 * @description 为 OSS 图片 URL 追加图片处理参数（质量、渐进显示等）
 * @param {string} url - 原始 OSS 图片地址（可为完整或相对路径，仅对 OSS 域名生效）
 * @param {string} processStr - 处理串，如 'quality,q_20/interlace,1'
 * @returns {string} - 带 x-oss-process 的 URL，非 OSS 则返回原 URL
 */
export const appendOssImageProcess = (url, processStr) => {
  if (!url || !processStr || !isOssUrl(url)) return url
  const fullUrl = url.startsWith('http') ? url : getOssUrl(url)
  if (isOssSignedUrl(fullUrl)) return fullUrl

  // 若已存在 x-oss-process，则合并处理串，避免重复参数：
  // ?x-oss-process=image/format,avif&x-oss-process=image/average-hue（❌）
  // ?x-oss-process=image/format,avif/average-hue（✅）
  try {
    const parseUrl = fullUrl.startsWith('//') ? `https:${fullUrl}` : fullUrl
    const urlObj = new URL(parseUrl)
    const existing = urlObj.searchParams.get('x-oss-process')

    if (existing && existing.startsWith('image/')) {
      const normalizedProcessStr = processStr.startsWith('image/')
        ? processStr.replace(/^image\//, '')
        : processStr
      const mergedProcess = `${existing}/${normalizedProcessStr}`

      // 手动构建 URL，避免 URLSearchParams 对 x-oss-process 进行编码，并移除所有旧的 x-oss-process
      const baseUrl = fullUrl.startsWith('//')
        ? `//${urlObj.host}${urlObj.pathname}`
        : urlObj.origin + urlObj.pathname
      const searchParams = []

      urlObj.searchParams.forEach((value, key) => {
        if (key !== 'x-oss-process') {
          searchParams.push(`${key}=${value}`)
        }
      })

      searchParams.push(`x-oss-process=${mergedProcess}`)
      return `${baseUrl}?${searchParams.join('&')}`
    }
  } catch (e) {
    // URL 解析失败则走兜底拼接逻辑
  }

  const separator = fullUrl.includes('?') ? '&' : '?'
  const normalized = processStr.startsWith('image/') ? processStr : `image/${processStr}`
  // 浏览器会自动处理 URL 编码，不需要手动编码
  return `${fullUrl}${separator}x-oss-process=${normalized}`
}

/**
 * @description 图片点击放大预览场景不使用 AVIF（解码与部分全屏预览体验），将 x-oss-process 中的 format,avif 改为 format,webp
 * @param {string} url
 * @returns {string}
 */
export const getOssUrlWithoutAvifForPreview = (url) => {
  if (!url || typeof url !== 'string') return url
  if (isOssSignedUrl(url)) return url
  if (!url.includes('format,avif')) return url
  return url.replace(/format,avif/g, 'format,webp')
}

/**
 * @description 获取 OSS 渐进式加载用低质量占位图 URL（小图+低质量+渐进）
 * @param {string} url - 原始 OSS 图片地址
 * @returns {string} - 占位图 URL，非 OSS 返回空字符串
 */
export const getOssPlaceholderUrl = (url) => {
  if (!isOssUrl(url)) return ''
  return appendOssImageProcess(url, 'resize,w_40/quality,q_20/format,jpg/interlace,1')
}

/**
 * @description 获取 OSS 渐进显示的全图 URL（仅加 interlace，JPG 渐进解码）
 * @param {string} url - 原始 OSS 图片地址
 * @returns {string} - 带渐进显示的 URL，非 OSS 返回原 URL
 */
export const getOssProgressiveUrl = (url) => {
  if (!url) return url
  if (!isOssUrl(url)) return url
  return appendOssImageProcess(url, 'interlace,1')
}

/**
 * @description 为 OSS 图片 URL 添加宽度限制（限制最大宽度为 1280px）
 * @param {string} url - 原始 OSS 图片地址（可能已包含其他处理参数）
 * @returns {string} - 带宽度限制的 URL，非 OSS 返回原 URL
 */
export const getOssLimitedWidthUrl = (url) => {
  if (!url) return url
  if (!isOssUrl(url)) return url
  if (isOssSignedUrl(url)) return url

  try {
    const urlObj = new URL(url)
    const processParam = urlObj.searchParams.get('x-oss-process')

    if (processParam && processParam.startsWith('image/')) {
      const existingProcess = processParam.replace('image/', '')
      if (existingProcess.includes(OSS_DISPLAY_RESIZE_W1280)) {
        return url
      }
      const newProcess = `${OSS_DISPLAY_RESIZE_W1280}/${existingProcess}`

      const baseUrl = urlObj.origin + urlObj.pathname
      const searchParams = []

      urlObj.searchParams.forEach((value, key) => {
        if (key !== 'x-oss-process') {
          searchParams.push(`${key}=${value}`)
        }
      })

      searchParams.push(`x-oss-process=image/${newProcess}`)
      return `${baseUrl}?${searchParams.join('&')}`
    }
  } catch (e) {
    // URL 解析失败，使用 appendOssImageProcess 处理
  }

  return appendOssImageProcess(url, OSS_DISPLAY_RESIZE_W1280)
}

/**
 * @description 检测浏览器是否支持指定图片格式
 * @param {string} format - 图片格式 ('webp' | 'avif')
 * @returns {Promise<boolean>} - 是否支持该格式
 */
export const checkImageFormatSupport = (format) => {
  if (!import.meta.client) return Promise.resolve(false)

  return new Promise((resolve) => {
    const img = new Image()
    img.onload = () => resolve(true)
    img.onerror = () => resolve(false)

    // 使用 data URI 来检测格式支持
    if (format === 'webp') {
      img.src =
        'data:image/webp;base64,UklGRjoAAABXRUJQVlA4IC4AAACyAgCdASoCAAIALmk0mk0iIiIiIgBoSygABc6WWgAA/veff/0PP8bA//LwYAAA'
    } else if (format === 'avif') {
      img.src =
        'data:image/avif;base64,AAAAIGZ0eXBhdmlmAAAAAGF2aWZtaWYxbWlhZk1BMUIAAADybWV0YQAAAAAAAAAoaGRscgAAAAAAAAAAcGljdAAAAAAAAAAAAAAAAGxpYmF2aWYAAAAADnBpdG0AAAAAAAEAAAAeaWxvYwAAAABEAAABAAEAAAABAAABGgAAAB0AAAAoaWluZgAAAAAAAQAAABppbmZlAgAAAAABAABhdjAxQ29sb3IAAAAAamlwcnAAAABLaXBjbwAAABRpc3BlAAAAAAAAAAIAAAACAAAAEHBpeGkAAAAAAwgICAAAAAxhdjFDgQ0MAAAAABNjb2xybmNseAACAAIAAYAAAAAXaXBtYQAAAAAAAAABAAEEAQKDBAAAACVtZGF0EgAKCBgABogQEAwgMg8f8D///8WfhwB8+ErK42A='
    } else {
      resolve(false)
    }
  })
}

/**
 * @description 获取浏览器支持的图片格式（按优先级：avif > webp > 原格式）
 * @returns {Promise<string>} - 支持的格式 ('avif' | 'webp' | '')
 */
let supportedFormatCache = null
export const getSupportedImageFormat = async () => {
  if (!import.meta.client) return ''
  if (supportedFormatCache !== null) return supportedFormatCache

  // 优先检测 avif
  const avifSupported = await checkImageFormatSupport('avif')
  if (avifSupported) {
    supportedFormatCache = 'avif'
    return 'avif'
  }

  // 其次检测 webp
  const webpSupported = await checkImageFormatSupport('webp')
  if (webpSupported) {
    supportedFormatCache = 'webp'
    return 'webp'
  }

  // 都不支持则返回空字符串，使用原格式
  supportedFormatCache = ''
  return ''
}

/**
 * @description 为 OSS 图片 URL 添加格式后缀（根据浏览器支持情况）
 * @param {string} url - 原始 OSS 图片地址（可能已包含其他处理参数）
 * @param {string} [format] - 指定格式 ('avif' | 'webp')，不传则自动检测
 * @param {{ isBanner?: boolean }} [options] - isBanner 为 true 时不加 resize,w_1280，且固定 format,webp
 * @returns {Promise<string>} - 带格式后缀的 URL，非 OSS 返回原 URL
 */
export const getOssFormatUrl = async (url, format, options = {}) => {
  if (!url) return url
  if (!isOssUrl(url)) return url
  if (isOssSignedUrl(url)) return url

  const isBanner = Boolean(options?.isBanner)

  // 确定要使用的格式（banner 固定 webp，与普通图按浏览器能力选 avif/webp 区分）
  let targetFormat = format
  if (!targetFormat) {
    targetFormat = isBanner ? 'webp' : await getSupportedImageFormat()
  } else if (isBanner && targetFormat === 'avif') {
    targetFormat = 'webp'
  }

  // 如果 URL 已有格式参数：banner 统一改为 webp；普通图保持原样
  if (url.includes('/format,')) {
    if (isBanner && !url.includes('format,webp')) {
      return url.replace(/\/format,[a-z0-9]+/gi, '/format,webp')
    }
    return url
  }

  // 如果不支持任何现代格式，返回原 URL（banner 已强制 webp，不会落到空）
  if (!targetFormat) return url

  // 检查 URL 是否已有 x-oss-process 参数
  try {
    const urlObj = new URL(url)
    const processParam = urlObj.searchParams.get('x-oss-process')
    if (processParam) {
      // 如果已有处理参数，需要在现有参数中添加格式
      // URLSearchParams.get() 已经自动解码了，所以 processParam 是解码后的值
      // 检查是否已经是 image/ 开头的格式
      if (processParam.startsWith('image/')) {
        // 普通图：现代格式（avif/webp）必须先限宽 1280；banner 不限宽、且仅 webp
        let body = processParam.slice('image/'.length)
        if (
          !isBanner &&
          (targetFormat === 'avif' || targetFormat === 'webp') &&
          !body.includes(OSS_DISPLAY_RESIZE_W1280)
        ) {
          body = `${OSS_DISPLAY_RESIZE_W1280}/${body}`
        }
        const newProcess = `image/${body}/format,${targetFormat}`

        // 手动构建 URL，避免 URLSearchParams 对格式参数进行编码
        const baseUrl = urlObj.origin + urlObj.pathname
        const searchParams = []

        // 收集其他查询参数（排除 x-oss-process）
        urlObj.searchParams.forEach((value, key) => {
          if (key !== 'x-oss-process') {
            searchParams.push(`${key}=${value}`)
          }
        })

        // 添加新的 x-oss-process 参数（不编码，让浏览器自动处理）
        searchParams.push(`x-oss-process=${newProcess}`)

        return `${baseUrl}?${searchParams.join('&')}`
      }
    }
  } catch (e) {
    // URL 解析失败，使用 appendOssImageProcess 处理
  }

  // 添加格式参数（使用 appendOssImageProcess 处理）；普通图 avif/webp 与限宽一并下发
  if (targetFormat === 'avif' || targetFormat === 'webp') {
    const processPrefix = isBanner ? '' : `${OSS_DISPLAY_RESIZE_W1280}/`
    return appendOssImageProcess(url, `${processPrefix}format,${targetFormat}`)
  }
  return appendOssImageProcess(url, `format,${targetFormat}`)
}

/**
 * @description 归一化图片资源字段，兼容字符串、数组以及携带 url/src 的对象
 * @param {string|Array|Record<string, any>} input - 原始图片资源
 * @returns {string}
 */
const normalizeOssSource = (input) => {
  if (!input) return ''

  if (typeof input === 'string') {
    return input.trim()
  }

  if (Array.isArray(input)) {
    for (const item of input) {
      const normalized = normalizeOssSource(item)
      if (normalized) return normalized
    }
    return ''
  }

  if (typeof input === 'object') {
    const candidateKeys = [
      'previewUrl',
      'url',
      'src',
      'path',
      'image',
      'picture',
      'originUrl',
      'fileUrl',
    ]
    for (const key of candidateKeys) {
      const normalized = normalizeOssSource(input[key])
      if (normalized) return normalized
    }
  }

  return ''
}

/**
 * @description 获取 OSS 图片完整路径（默认展示图会带 resize,w_1280 + 现代格式）
 * @param {string|Array|Record<string, any>} url - 图片相对路径、完整路径或后端返回的图片资源结构
 * @param {{ isBanner?: boolean }} [options] - isBanner 时固定 OSS format,webp 且不限宽；全宽轮播与顶栏请改用 getOssBannerUrl()
 * @returns {string} - 处理后的完整路径
 */
export const getOssUrl = (url, options = {}) => {
  const isBanner = Boolean(options?.isBanner)
  const normalizedUrl = normalizeOssSource(url)
  if (!normalizedUrl) return ''
  if (normalizedUrl.startsWith('data:')) return normalizedUrl

  const isAbsoluteUrl = normalizedUrl.startsWith('http') || normalizedUrl.startsWith('//')
  const cleanUrl = isAbsoluteUrl
    ? normalizedUrl
    : normalizedUrl.startsWith('/')
      ? normalizedUrl
      : `/${normalizedUrl}`
  const fullUrl = isAbsoluteUrl ? normalizedUrl : `${DEFAULT_STATIC_ASSET_ORIGIN}${cleanUrl}`

  // webp/avif 兼容处理：不改变函数签名（保持同步），在已知浏览器支持能力时做最佳格式选择
  if (!fullUrl.startsWith('http') && !fullUrl.startsWith('//')) return fullUrl
  if (!isOssUrl(fullUrl)) return fullUrl
  if (isOssSignedUrl(fullUrl)) return fullUrl

  // average-hue 用于获取图片平均色值：存在该处理时不再叠加 format 等其他处理，避免影响结果
  try {
    const parseUrl = fullUrl.startsWith('//') ? `https:${fullUrl}` : fullUrl
    const urlObj = new URL(parseUrl)
    const processParam = urlObj.searchParams.get('x-oss-process')
    if (processParam && processParam.includes('average-hue')) return fullUrl
  } catch (e) {
    // ignore
  }

  if (fullUrl.includes('/format,')) {
    if (isBanner) {
      if (fullUrl.includes('format,webp')) return fullUrl
      return fullUrl.replace(/\/format,[a-z0-9]+/gi, '/format,webp')
    }
    return fullUrl
  }

  const pathWithoutQuery = fullUrl.split('?')[0] || ''
  const extMatch = pathWithoutQuery.toLowerCase().match(/\.([a-z0-9]+)$/)
  const ext = extMatch ? extMatch[1] : ''
  const pathMatch = pathWithoutQuery.match(/^https?:\/\/[^/]+(?<assetPath>\/.*)$/i)
  const normalizedAssetPath = pathMatch?.groups?.assetPath || cleanUrl

  if (shouldDisableStaticAssetOssFormat(normalizedAssetPath)) return fullUrl

  // 仅对常见图片做格式处理，避免影响下载链接等非图片资源
  const IMAGE_EXT_SET = new Set(['jpg', 'jpeg', 'png', 'gif', 'bmp', 'ico', 'webp', 'avif'])
  if (!IMAGE_EXT_SET.has(ext)) return fullUrl

  // Banner / 通栏：固定 OSS 输出 webp（上面已处理 URL 内已有 format 的情况）
  if (isBanner) {
    if (ext === 'webp') return fullUrl
    return appendOssImageProcess(fullUrl, 'format,webp')
  }

  // SSR cannot negotiate the browser's image capabilities. WebP is safe for the supported
  // browser baseline and keeps first-render markup aligned with the client fallback below.
  if (import.meta.server) {
    return appendOssImageProcess(fullUrl, `${OSS_DISPLAY_RESIZE_W1280}/format,webp`)
  }

  // 触发一次异步检测（不阻塞当前调用），后续调用可直接使用缓存
  if (supportedFormatCache === null) {
    void getSupportedImageFormat()
  }

  // 兼容降级：源资源是 webp/avif 时，若浏览器不支持则降级为可显示格式（普通图转码限宽 1280）
  const formatWithOptionalResize = (fmt) => `${OSS_DISPLAY_RESIZE_W1280}/format,${fmt}`

  if (ext === 'avif') {
    if (supportedFormatCache === 'avif') return fullUrl
    if (supportedFormatCache === 'webp') {
      return appendOssImageProcess(fullUrl, formatWithOptionalResize('webp'))
    }
    return appendOssImageProcess(fullUrl, formatWithOptionalResize('jpg'))
  }
  if (ext === 'webp') {
    if (supportedFormatCache === 'webp' || supportedFormatCache === 'avif') return fullUrl
    return appendOssImageProcess(fullUrl, formatWithOptionalResize('jpg'))
  }

  // 能力升级：当浏览器支持 modern 格式且源资源不是 webp/avif 时，自动请求 modern 格式（普通图带 1280 限宽）
  if (supportedFormatCache === 'avif' || supportedFormatCache === 'webp') {
    return appendOssImageProcess(fullUrl, formatWithOptionalResize(supportedFormatCache))
  }

  // 能力未知时回退原图，避免旧 Safari / 国产内核强制 webp 裂图
  return fullUrl
}

/**
 * @description 获取 OSS 图片原始地址，不追加压缩、缩放或格式转换参数；若传入地址已带 x-oss-process，会自动移除
 * @param {string|Array|Record<string, any>} url - 图片相对路径、完整路径或后端返回的图片资源结构
 * @returns {string} - 原始 OSS 图片地址
 */
export const getOssOriginUrl = (url) => {
  const normalizedUrl = normalizeOssSource(url)
  if (!normalizedUrl) return ''
  if (normalizedUrl.startsWith('data:')) return normalizedUrl

  const isAbsoluteUrl = normalizedUrl.startsWith('http') || normalizedUrl.startsWith('//')
  const cleanUrl = isAbsoluteUrl
    ? normalizedUrl
    : normalizedUrl.startsWith('/')
      ? normalizedUrl
      : `/${normalizedUrl}`
  const fullUrl = isAbsoluteUrl ? normalizedUrl : `${DEFAULT_STATIC_ASSET_ORIGIN}${cleanUrl}`
  const parseUrl = fullUrl.startsWith('//') ? `https:${fullUrl}` : fullUrl

  if (!parseUrl.startsWith('http')) return fullUrl
  if (!isOssUrl(parseUrl)) return fullUrl
  if (isOssSignedUrl(parseUrl)) return fullUrl

  try {
    const urlObj = new URL(parseUrl)
    urlObj.searchParams.delete('x-oss-process')
    return urlObj.toString()
  } catch {
    return fullUrl
  }
}

/**
 * @description 全宽轮播 / 通栏顶栏背景等 OSS 图：不限宽，且 OSS 处理链固定为 format,webp（不用 avif）。业务侧须用本函数。
 * @param {string|Array|Record<string, any>} url - 同 getOssUrl
 * @returns {string}
 */
export const getOssBannerUrl = (url) => getOssUrl(url, { isBanner: true })

/**
 * @description 组装region为完整地址
 * @param {Object|string} region - 地址信息对象或字符串
 * @returns {string} - 完整地址字符串
 */
export const assembleRegionAddress = (region) => {
  if (!region) return ''
  if (typeof region === 'string') return region
  const parts = []
  if (region.province) parts.push(region.province)
  if (region.city) parts.push(region.city)
  if (region.area) parts.push(region.area)
  if (region.street) parts.push(region.street)
  return parts.join(' ')
}

/**
 * 行首行政街道/乡镇段：非空白名称 + 后缀（长后缀写在短后缀前，避免误截）
 * 基于 /^([^\s]+(?:街道|镇|乡|民族乡|苏木|办事处|街道办))/ 优化顺序
 */
const ADMIN_STREET_LINE_PREFIX_RE = /^[^\s]+(?:街道办|民族乡|街道|办事处|镇|乡|苏木)/

/**
 * @description 仅按正则去掉行首「某某街道/镇/乡…」段，用于 addr 与地区街道拆分
 * @param {string} text
 * @returns {string}
 */
export function stripLeadingAdminStreetByRegex(text) {
  if (text == null || text === '') return ''
  const t = String(text).trim()
  const m = t.match(ADMIN_STREET_LINE_PREFIX_RE)
  if (!m) return t
  return t
    .slice(m[0].length)
    .trim()
    .replace(/^[,\s\u3000，、；;]+/u, '')
    .trim()
}

function stripByKnownStreetName(fullAddr, street) {
  if (!fullAddr) return ''
  let full = String(fullAddr).trim()
  const s = String(street || '').trim()
  if (!s) return full

  const stripAfterPrefix = (text, prefix) => {
    if (!prefix || !text.startsWith(prefix)) return null
    return text
      .slice(prefix.length)
      .trim()
      .replace(/^[,\s\u3000，、；;]+/u, '')
      .trim()
  }

  let rest = stripAfterPrefix(full, s)
  if (rest !== null) return rest

  const fullN = full.normalize('NFC')
  const sN = s.normalize('NFC')
  rest = stripAfterPrefix(fullN, sN)
  if (rest !== null) return rest

  try {
    const escaped = s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    const re = new RegExp(`^${escaped}[\\s\\u3000,，、]*`)
    if (re.test(full)) {
      return full
        .replace(re, '')
        .trim()
        .replace(/^[,\s\u3000，、]+/u, '')
        .trim()
    }
  } catch {
    // ignore
  }

  return full
}

/**
 * @description 详细地址回显：先按行政区划返回的街道名去掉前缀，再按正则去掉行首街道段，避免与「地区」里街道重复
 * @param {string} fullAddr
 * @param {string} [street] - 当前选中的街道名称（可选）
 * @returns {string}
 */
export function stripStreetPrefixFromDetailAddr(fullAddr, street) {
  let rest = stripByKnownStreetName(fullAddr == null ? '' : String(fullAddr).trim(), street || '')
  rest = stripLeadingAdminStreetByRegex(rest)
  return rest
}

/**
 * @description 数字补零
 */
export const timeFormat = (num) => {
  return num < 10 ? `0${num}` : num
}

/**
 * 获取倒计时对象
 * @param {Object} item 时间对象 {startTime, endTime}
 * @returns {Object} 格式化后的倒计时对象
 */
export const getCountdown = (item) => {
  const newTime = new Date().getTime()
  let time = 0
  let type = 0 // 0.未开始 1.进行中 2.已结束
  const startTimeStamp = item.startTime
  const endTimeStamp = item.endTime

  if (newTime >= startTimeStamp) {
    // 活动已经开始
    if (endTimeStamp > newTime) {
      // 秒杀正在进行
      time = (endTimeStamp - newTime) / 1000
      type = 1
    } else {
      // 活动已结束
      time = 0
      type = 2
    }
  } else {
    // 活动未开始
    time = (startTimeStamp - newTime) / 1000
    type = 0
  }

  const day = Math.floor(time / (60 * 60 * 24))
  const hou = Math.floor((time % (60 * 60 * 24)) / 3600)
  const min = Math.floor(((time % (60 * 60 * 24)) % 3600) / 60)
  const sec = Math.floor(((time % (60 * 60 * 24)) % 3600) % 60)

  return {
    day,
    hou,
    min,
    sec,
    type,
    totalSeconds: time,
  }
}

/**
 * 将总秒数格式化为天、小时、分钟（分钟向上取整）
 * @param {number} totalSeconds - 总秒数
 * @returns {Object} { d, h, m } 包含天、小时、分钟
 */
export const getRoundedMinutesCountdown = (totalSeconds) => {
  if (!totalSeconds || totalSeconds <= 0) return { d: 0, h: 0, m: 0 }
  const totalMinutes = Math.ceil(totalSeconds / 60)
  const d = Math.floor(totalMinutes / (60 * 24))
  const h = Math.floor((totalMinutes % (60 * 24)) / 60)
  const m = totalMinutes % 60
  return { d, h, m }
}

/**
 * @description 将剩余秒数格式化为与订单详情/收银台一致的倒计时文案（天+补零时分秒；不足天按段省略）
 * @param {number} seconds - 剩余秒数（非有限数按 0 处理）
 * @param {Object} [options]
 * @param {boolean} [options.blankWhenZero] - 为 true 且剩余≤0 时返回空字符串
 * @param {boolean} [options.includeSeconds=true] - 为 false 时只显示到天/时/分（截断秒；剩余不足 1 分钟且仍大于 0 秒时返回「小于1分钟」）
 * @returns {string}
 */
export const formatRemainingDurationText = (seconds, options = {}) => {
  const includeSeconds = options.includeSeconds !== false
  const s = Math.floor(Number(seconds) || 0)
  if (s <= 0) {
    if (options.blankWhenZero) return ''
    return includeSeconds ? '0秒' : '0分'
  }
  const d = Math.floor(s / (24 * 3600))
  const h = Math.floor((s % (24 * 3600)) / 3600)
  const m = Math.floor((s % 3600) / 60)
  const sec = s % 60
  if (!includeSeconds) {
    if (d > 0) {
      return `${d}天${String(h).padStart(2, '0')}时${String(m).padStart(2, '0')}分`
    }
    if (h > 0) {
      return `${String(h).padStart(2, '0')}时${String(m).padStart(2, '0')}分`
    }
    if (m > 0) {
      return `${String(m).padStart(2, '0')}分`
    }
    return '小于1分钟'
  }
  if (d > 0) {
    return `${d}天${String(h).padStart(2, '0')}时${String(m).padStart(2, '0')}分${String(sec).padStart(2, '0')}秒`
  }
  if (h > 0) {
    return `${String(h).padStart(2, '0')}时${String(m).padStart(2, '0')}分${String(sec).padStart(2, '0')}秒`
  }
  if (m > 0) {
    return `${String(m).padStart(2, '0')}分${String(sec).padStart(2, '0')}秒`
  }
  return `${sec}秒`
}

/**
 * @description 订单支付等场景倒计时文案（与 {@link formatRemainingDurationText} 相同，保留历史命名）
 * @param {number} seconds
 * @param {Object} [options]
 * @returns {string}
 */
export const formatOrderPayCountdownText = (seconds, options = {}) =>
  formatRemainingDurationText(seconds, options)

/**
 * @description Arco `a-countdown` 的 format 字符串：与订单列表待付款倒计时分段规则一致（按距结束毫秒差）
 * @param {number} targetMs - 结束时间戳（毫秒）
 * @param {number} [nowMs=Date.now()] - 当前时间戳（毫秒）
 * @returns {string} Arco Countdown 支持的 format
 */
export const getArcoCountdownFormatUntilEnd = (targetMs, nowMs = Date.now()) => {
  const target = Number(targetMs) || 0
  if (!target) return 's秒'
  const diff = target - nowMs
  const oneDay = 24 * 3600 * 1000
  const oneHour = 3600 * 1000
  const oneMinute = 60 * 1000
  if (diff >= oneDay) return 'D天HH时mm分ss秒'
  if (diff >= oneHour) return 'HH时mm分ss秒'
  if (diff >= oneMinute) return 'mm分ss秒'
  return 's秒'
}

/**
 * 获取倒计时数组
 * @param {Array} endTimeList 结束时间数组 [{sTime, eTime}]
 * @returns {Array} 格式化后的倒计时数组
 */
export const getCountdownArr = (endTimeList) => {
  return endTimeList.map((o) => {
    return getCountdown({
      startTime: dateToTimestamp(o.sTime),
      endTime: dateToTimestamp(o.eTime),
    })
  })
}
export const dateToTimestamp = (dateStr) => {
  if (dateStr === null || dateStr === undefined || dateStr === '') return ''

  // 支持秒级/毫秒级时间戳（数字或可转数字字符串）
  if (typeof dateStr === 'number') {
    return Number.isFinite(dateStr) ? (dateStr < 1e12 ? dateStr * 1000 : dateStr) : ''
  }
  if (dateStr instanceof Date) {
    const ts = dateStr.getTime()
    return Number.isFinite(ts) ? ts : ''
  }

  const raw = String(dateStr).trim()
  if (!raw) return ''

  // 先尝试原始字符串解析（Android/现代浏览器对 ISO8601 支持更好）
  const direct = Date.parse(raw)
  if (Number.isFinite(direct)) return direct

  // iOS/Safari 常见兼容：替换分隔符、处理 T、修正时区 "+08:00" -> "+0800"
  const normalized = raw
    .replace(/\.|-/g, '/')
    .replace('T', ' ')
    .replace(/([+-]\d{2}):(\d{2})$/, '$1$2')

  const normalizedParsed = Date.parse(normalized)
  if (Number.isFinite(normalizedParsed)) return normalizedParsed

  const ts = new Date(normalized).getTime()
  return Number.isFinite(ts) ? ts : ''
}

/**
 * @param {Record<string, unknown>} info - OSS image/info 返回的 JSON
 */
const parseOssImageInfoSize = (info) => {
  if (!info || typeof info !== 'object') return { w: 0, h: 0 }
  const iw = /** @type {{ value?: string }} */ (info.ImageWidth)
  const ih = /** @type {{ value?: string }} */ (info.ImageHeight)
  const w = Number(iw?.value ?? info.ImageWidth ?? 0)
  const h = Number(ih?.value ?? info.ImageHeight ?? 0)
  return { w, h }
}

/**
 * 与「居中 1280 内容区」对齐：在水平居中的 centerBand 条带内，再取宽 sliceWidth 的一条竖带做采样（桌面文案在左用 left，移动端文案居中用 center）
 * @param {'full'|'upper'|'lower'} [verticalSample='full'] - upper：只采图像上半区，适配移动端文案在上方的布局；lower：只采图像偏下区域
 * @returns {string} image/crop 处理串（不含 average-hue），尺寸无效时返回空串
 */
const buildCenterBandSliceCropProcess = (
  w,
  h,
  centerBand = 1280,
  sliceWidth = 640,
  sliceAlign = 'left',
  verticalSample = 'full'
) => {
  if (!w || !h || sliceWidth <= 0 || centerBand <= 0) return ''
  let cropY = 0
  let cropH = h
  if (verticalSample === 'upper') {
    // 移动端文案位于 Banner 上方，采上半区来决定反色文字，避免被下方主体画面干扰
    cropH = Math.max(1, Math.ceil(h * 0.5))
  } else if (verticalSample === 'lower') {
    // 文案在轮播偏下，略过顶部约 35%，采剩余区域（与商城移动 Banner 标题区大致对齐）
    cropY = Math.floor(h * 0.35)
    cropH = Math.max(1, h - cropY)
  }
  let cropX
  let cropW
  if (w >= centerBand) {
    const bandLeft = Math.floor((w - centerBand) / 2)
    cropW = Math.min(sliceWidth, centerBand)
    if (sliceAlign === 'center') {
      cropX = bandLeft + Math.floor((centerBand - cropW) / 2)
    } else {
      cropX = bandLeft
    }
  } else {
    cropW = Math.min(sliceWidth, w)
    if (sliceAlign === 'center') {
      cropX = Math.floor((w - cropW) / 2)
    } else {
      cropX = 0
    }
  }
  return `image/crop,x_${cropX},y_${cropY},w_${cropW},h_${cropH}`
}

/**
 * 对图片 URL 解码后在浏览器内做像素算术平均亮度（OSS image/average-hue 与真实均值可能严重偏离，尤其 JPG）
 * @param {string} imageUrl
 * @param {number} [brightnessThreshold=179] - BT.709 亮度阈值，超过则判为浅色背景（深色字）
 * @returns {Promise<'light'|'dark'|null>}
 */
const getThemeFromImagePixels = (imageUrl, brightnessThreshold = 179) => {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return Promise.resolve(null)
  }
  return new Promise((resolve) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => {
      try {
        const w = img.naturalWidth
        const h = img.naturalHeight
        if (!w || !h) {
          resolve(null)
          return
        }
        const canvas = document.createElement('canvas')
        canvas.width = w
        canvas.height = h
        const ctx = canvas.getContext('2d')
        if (!ctx) {
          resolve(null)
          return
        }
        ctx.drawImage(img, 0, 0)
        const { data } = ctx.getImageData(0, 0, w, h)
        let r = 0
        let g = 0
        let b = 0
        const n = data.length / 4
        for (let i = 0; i < data.length; i += 4) {
          r += data[i]
          g += data[i + 1]
          b += data[i + 2]
        }
        const brightness = 0.2126 * (r / n) + 0.7152 * (g / n) + 0.0722 * (b / n)
        resolve(brightness > brightnessThreshold ? 'light' : 'dark')
      } catch {
        resolve(null)
      }
    }
    img.onerror = () => resolve(null)
    img.src = imageUrl
  })
}

/**
 * @description 获取图片主色调并判断文字主题
 * @param {string} imageUrl - 图片地址
 * @param {{ centerBandWidth?: number, leftSliceWidth?: number, sliceAlign?: 'left'|'center', verticalSample?: 'full'|'upper'|'lower', brightnessThreshold?: number }} [options] - 若同时传入正数 centerBandWidth、leftSliceWidth，则先 image/info 再按居中条带内 crop（verticalSample=upper/lower 时只采图像上半部/下半部），在浏览器内对裁剪图做像素算术平均；brightnessThreshold 默认 179（移动端可略调低）；失败时再回退 OSS average-hue
 * @returns {Promise<string>} - 'light' | 'dark'
 */
export const getImageTheme = async (imageUrl, options = {}) => {
  if (!imageUrl || !isOssUrl(imageUrl)) return 'dark'
  if (isOssSignedUrl(imageUrl)) return 'dark'
  try {
    const urlObj = new URL(imageUrl.startsWith('//') ? `https:${imageUrl}` : imageUrl)
    urlObj.searchParams.delete('x-oss-process')

    const {
      centerBandWidth,
      leftSliceWidth,
      sliceAlign = 'left',
      verticalSample = 'full',
      brightnessThreshold: btOpt,
    } = options
    const brightnessThreshold = typeof btOpt === 'number' && Number.isFinite(btOpt) ? btOpt : 179
    const useCenterBandSlice =
      typeof centerBandWidth === 'number' &&
      centerBandWidth > 0 &&
      typeof leftSliceWidth === 'number' &&
      leftSliceWidth > 0

    let cropProcess = ''
    if (useCenterBandSlice) {
      urlObj.searchParams.set('x-oss-process', 'image/info')
      const infoRes = await fetch(urlObj.toString())
      const info = await infoRes.json()
      const { w, h } = parseOssImageInfoSize(info)
      const align = sliceAlign === 'center' ? 'center' : 'left'
      const vSample =
        verticalSample === 'upper' ? 'upper' : verticalSample === 'lower' ? 'lower' : 'full'
      cropProcess = buildCenterBandSliceCropProcess(
        w,
        h,
        centerBandWidth,
        leftSliceWidth,
        align,
        vSample
      )
    }

    if (cropProcess) {
      urlObj.searchParams.set('x-oss-process', cropProcess)
      const pixelTheme = await getThemeFromImagePixels(urlObj.toString(), brightnessThreshold)
      if (pixelTheme) return pixelTheme
    }

    const hueProcess = cropProcess ? `${cropProcess}/average-hue` : 'image/average-hue'
    urlObj.searchParams.set('x-oss-process', hueProcess)
    const response = await fetch(urlObj.toString())
    const data = await response.json()

    // data.RGB 格式如 "0xbbcd7f"
    const rgbHex = String(data.RGB).replace(/^0x/i, '')
    const r = parseInt(rgbHex.substring(0, 2), 16)
    const g = parseInt(rgbHex.substring(2, 4), 16)
    const b = parseInt(rgbHex.substring(4, 6), 16)

    // 计算亮度 (Perceived brightness)
    // 使用 ITU-R BT.709 公式
    const brightness = 0.2126 * r + 0.7152 * g + 0.0722 * b

    // 亮度阈值默认 179（约 0.7 * 255）；超过阈值认为是浅色背景，返回 'light'（对应深色文字）
    return brightness > brightnessThreshold ? 'light' : 'dark'
  } catch (error) {
    console.warn('获取图片主色调失败:', error)
    return 'dark'
  }
}

/**
 * @description 复制文本到剪贴板
 * @param {string} text - 要复制的文本
 * @returns {boolean} - 是否成功
 */
export const copyText = (text) => {
  if (!import.meta.client || !text) return false
  const textArea = document.createElement('textarea')
  textArea.value = text
  document.body.appendChild(textArea)
  textArea.select()
  try {
    document.execCommand('copy')
    document.body.removeChild(textArea)
    return true
  } catch (err) {
    document.body.removeChild(textArea)
    return false
  }
}
